import {
  type MouseEvent,
  type ReactNode,
  type Ref,
  useLayoutEffect,
  useRef,
} from "react";
import * as elementUtils from "@/common/utils/elementUtils";

// The tab cursor of the control bar and of the favorite image picker
const CURSOR_CLASS =
  "cursor highlight-bg-1 tw:[.favorite-image-picker_&]:absolute tw:[.favorite-image-picker_&]:top-0 tw:[.favorite-image-picker_&]:right-[2px] tw:[.favorite-image-picker_&]:h-[33.33%] tw:[.favorite-image-picker_&]:w-[2px] tw:[.control-bar_.control-bar-section_&]:absolute tw:[.control-bar_.control-bar-section_&]:top-0 tw:[.control-bar_.control-bar-section_&]:left-0 tw:[.control-bar_.control-bar-section_&]:h-[35px] tw:[.control-bar_.control-bar-section_&]:w-[2px] tw:[.portrait_.control-bar_.control-bar-section_&]:top-auto tw:[.portrait_.control-bar_.control-bar-section_&]:bottom-0 tw:[.portrait_.control-bar_.control-bar-section_&]:h-[2px] tw:[.portrait_.control-bar_.control-bar-section_&]:w-[35px]";

export const orientationType = {
  PORTRAIT: 0,
  LANDSCAPE: 1,
};

type RadioButtonBarProps = {
  name?: string;
  index?: number;
  orientation?: number;
  className?: string;
  nodeRef?: Ref<HTMLDivElement>;
  children?: ReactNode;
  onSetValue?: (name: string | undefined, index: number) => void;
};

// Real tabs only; separator dividers (e.g. the Pixie divider) are skipped so an
// index maps to tabs, not raw DOM children.
function indexableNodes(wrapper: HTMLElement | null): HTMLElement[] {
  if (!wrapper || !wrapper.childNodes) {
    return [];
  }
  const out: HTMLElement[] = [];
  const nodes = wrapper.childNodes;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i] as HTMLElement;
    if (node.nodeType !== 1) {
      continue;
    }
    if (node.classList && node.classList.contains("separator")) {
      continue;
    }
    out.push(node);
  }
  return out;
}

export default function RadioButtonBar({
  name,
  index,
  orientation,
  className,
  nodeRef,
  children,
  onSetValue,
}: RadioButtonBarProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const cursorWasMounted = useRef(false);

  // `fresh` places the cursor without animating in from its default spot
  // (top / 35px). Later selections pass fresh=false so the line slides.
  const updateCursor = (fresh: boolean) => {
    const cursor = cursorRef.current;
    if (!cursor) {
      return;
    }
    if (fresh) {
      cursor.style.transition = "none";
    }
    const barOrientation = orientation ? orientation : orientationType.PORTRAIT;
    const button = indexableNodes(wrapperRef.current)[index as number];
    let offset: number;
    let size: number;
    let tr: string;

    if (barOrientation === orientationType.PORTRAIT) {
      offset = button ? button.offsetTop : 0;
      size = button ? button.offsetHeight : 10;
      tr = `translate(0,${offset}px)`;
      cursor.style.width = "";
      cursor.style.height = `${size}px`;
    } else {
      offset = button ? button.offsetLeft : 0;
      size = button ? button.offsetWidth : 10;
      tr = `translate(${offset}px, 0)`;
      cursor.style.width = `${size}px`;
      cursor.style.height = "";
    }

    cursor.style.transform = tr;
    cursor.style.webkitTransform = tr;

    if (fresh) {
      // Commit the un-transitioned styles, then restore the CSS transition.
      void cursor.offsetHeight;
      cursor.style.transition = "";
    }
  };

  // Runs after every render, before paint. The cursor is "fresh" only when it
  // just appeared (index went from <0 to a tab); a move between mounted tabs slides.
  useLayoutEffect(() => {
    const mounted = !!cursorRef.current;
    const fresh = mounted && !cursorWasMounted.current;
    cursorWasMounted.current = mounted;
    updateCursor(fresh);
  });

  const onClick = (e: MouseEvent) => {
    const nodes = indexableNodes(wrapperRef.current);
    for (let n = 0; n < nodes.length; n++) {
      if (elementUtils.hasChild(nodes[n], e.target as Node)) {
        if (onSetValue) {
          onSetValue(name, n);
        }
        return;
      }
    }
  };

  const childCount = (children as ArrayLike<unknown> | undefined)?.length;
  const hasCursor =
    !!children && (childCount as number) > 1 && (index as number) >= 0;

  const classNames = ["toggle-button-bar"];

  if (className) {
    classNames.push(className);
  }

  return (
    <div className={classNames.join(" ")} ref={nodeRef}>
      <div
        className="radio-button-wrapper tw:[.favorite-image-picker_&]:absolute tw:[.favorite-image-picker_&]:inset-0 tw:[.favorite-image-picker_&]:mr-[2px] tw:[.favorite-image-picker_&]:flex tw:[.favorite-image-picker_&]:flex-col tw:[.favorite-image-picker_&]:items-stretch tw:[.favorite-image-picker_&]:bg-[#fff] tw:[.portrait_.control-bar_&]:flex tw:[.portrait_.control-bar_&]:h-full tw:[.portrait_.control-bar_&]:w-auto"
        ref={wrapperRef}
        role="presentation"
        onClick={onClick}
      >
        {children}
      </div>
      {hasCursor && <div ref={cursorRef} className={CURSOR_CLASS} />}
    </div>
  );
}
